/**
 * CR283: Merchant Detail Page
 * Route: /admin/merchants/:merchantKey
 */

import { useParams, Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useMerchantDetail,
  useMerchantSources,
  useMerchantDirectoryAliases,
  useMerchantAuditLog,
} from "@/hooks/useMerchantsDirectory";
import { MerchantOverviewCard } from "@/components/admin/merchant-directory/MerchantOverviewCard";
import { MerchantBrandingCard } from "@/components/admin/merchant-directory/MerchantBrandingCard";
import { MerchantScanCard } from "@/components/admin/merchant-directory/MerchantScanCard";
import { MerchantAliasesCard } from "@/components/admin/merchant-directory/MerchantAliasesCard";
import { MerchantDiagnosticsCard } from "@/components/admin/merchant-directory/MerchantDiagnosticsCard";
import { MerchantAuditLogCard } from "@/components/admin/merchant-directory/MerchantAuditLogCard";

export default function MerchantDetail() {
  const { merchantKey } = useParams<{ merchantKey: string }>();
  const { isAdmin } = useAuth();

  const { data: merchant, isLoading: loadingMerchant } = useMerchantDetail(merchantKey);
  const { data: sources = [], isLoading: loadingSources } = useMerchantSources(merchantKey);
  const { data: aliases = [], isLoading: loadingAliases } = useMerchantDirectoryAliases(merchantKey);
  const { data: auditLog = [], isLoading: loadingAudit } = useMerchantAuditLog(merchantKey);

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="text-center py-12 text-muted-foreground">
          Admin access required
        </div>
      </AdminLayout>
    );
  }

  if (loadingMerchant) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid gap-6 md:grid-cols-2">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!merchant) {
    return (
      <AdminLayout>
        <div className="space-y-4">
          <Button variant="ghost" asChild>
            <Link to="/admin/merchants/directory">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Directory
            </Link>
          </Button>
          <div className="text-center py-12 text-muted-foreground">
            Merchant not found: {merchantKey}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/admin/merchants/directory">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Link>
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{merchant.display_name}</h1>
            <p className="text-sm text-muted-foreground font-mono">{merchant.merchant_key}</p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Left Column */}
          <div className="space-y-6">
            <MerchantOverviewCard merchant={merchant} />
            <MerchantBrandingCard merchant={merchant} />
            <MerchantAliasesCard
              merchantKey={merchant.merchant_key}
              aliases={aliases}
              loading={loadingAliases}
            />
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            <MerchantScanCard
              merchant={merchant}
              sources={sources}
              loading={loadingSources}
            />
            <MerchantDiagnosticsCard merchant={merchant} />
            <MerchantAuditLogCard
              auditLog={auditLog}
              loading={loadingAudit}
            />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
